/**
 * City search service - Uses dynamic API for comprehensive Canada/USA city coverage
 * Falls back to local search if API is unavailable
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache for API results
const searchCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Search cities using the backend API (GeoNames integration)
 * @param {string} query - Search query (minimum 2 characters)
 * @param {string} country - Country codes (default: "CA,US")
 * @param {number} maxResults - Maximum results to return (default: 15)
 * @returns {Promise<Array>} Array of city objects
 */
export const searchCitiesAPI = async (query, country = "CA,US", maxResults = 15) => {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const searchTerm = query.trim();
  const cacheKey = `${searchTerm}_${country}_${maxResults}`;
  
  // Check cache first
  const cached = searchCache.get(cacheKey);
  if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
    return cached.results;
  }

  try {
    const response = await fetch(
      `${API_URL}/api/cities/search?q=${encodeURIComponent(searchTerm)}&country=${country}&max_results=${maxResults}`
    );
    
    if (!response.ok) {
      throw new Error('API request failed');
    }
    
    const data = await response.json();
    const results = data.cities || [];
    
    // Cache the results
    searchCache.set(cacheKey, {
      results,
      timestamp: Date.now()
    });
    
    return results;
  } catch (error) {
    console.error('City search API error:', error);
    // Fall back to local search on error
    return searchCitiesLocal(query);
  }
};

/**
 * Local fallback search using a simplified static list
 * Used when API is unavailable
 */
const localCities = [
  // Québec - Principales villes
  { city: "Montréal", province: "QC", country: "CA", fullName: "Montréal, QC, Canada" },
  { city: "Québec", province: "QC", country: "CA", fullName: "Québec, QC, Canada" },
  { city: "Laval", province: "QC", country: "CA", fullName: "Laval, QC, Canada" },
  { city: "Gatineau", province: "QC", country: "CA", fullName: "Gatineau, QC, Canada" },
  { city: "Longueuil", province: "QC", country: "CA", fullName: "Longueuil, QC, Canada" },
  { city: "Sherbrooke", province: "QC", country: "CA", fullName: "Sherbrooke, QC, Canada" },
  { city: "Trois-Rivières", province: "QC", country: "CA", fullName: "Trois-Rivières, QC, Canada" },
  { city: "Saguenay", province: "QC", country: "CA", fullName: "Saguenay, QC, Canada" },
  { city: "Lévis", province: "QC", country: "CA", fullName: "Lévis, QC, Canada" },
  { city: "Terrebonne", province: "QC", country: "CA", fullName: "Terrebonne, QC, Canada" },
  { city: "Brossard", province: "QC", country: "CA", fullName: "Brossard, QC, Canada" },
  { city: "Repentigny", province: "QC", country: "CA", fullName: "Repentigny, QC, Canada" },
  { city: "Boucherville", province: "QC", country: "CA", fullName: "Boucherville, QC, Canada" },
  { city: "L'Assomption", province: "QC", country: "CA", fullName: "L'Assomption, QC, Canada" },
  { city: "Mascouche", province: "QC", country: "CA", fullName: "Mascouche, QC, Canada" },
  { city: "Saint-Jérôme", province: "QC", country: "CA", fullName: "Saint-Jérôme, QC, Canada" },
  { city: "Drummondville", province: "QC", country: "CA", fullName: "Drummondville, QC, Canada" },
  { city: "Granby", province: "QC", country: "CA", fullName: "Granby, QC, Canada" },
  { city: "Saint-Jean-sur-Richelieu", province: "QC", country: "CA", fullName: "Saint-Jean-sur-Richelieu, QC, Canada" },
  { city: "Blainville", province: "QC", country: "CA", fullName: "Blainville, QC, Canada" },
  { city: "Châteauguay", province: "QC", country: "CA", fullName: "Châteauguay, QC, Canada" },
  { city: "Saint-Hyacinthe", province: "QC", country: "CA", fullName: "Saint-Hyacinthe, QC, Canada" },
  { city: "Mirabel", province: "QC", country: "CA", fullName: "Mirabel, QC, Canada" },
  { city: "Rimouski", province: "QC", country: "CA", fullName: "Rimouski, QC, Canada" },
  { city: "Shawinigan", province: "QC", country: "CA", fullName: "Shawinigan, QC, Canada" },
  { city: "Victoriaville", province: "QC", country: "CA", fullName: "Victoriaville, QC, Canada" },
  { city: "Joliette", province: "QC", country: "CA", fullName: "Joliette, QC, Canada" },
  { city: "Rouyn-Noranda", province: "QC", country: "CA", fullName: "Rouyn-Noranda, QC, Canada" },
  { city: "Val-d'Or", province: "QC", country: "CA", fullName: "Val-d'Or, QC, Canada" },
  { city: "Sorel-Tracy", province: "QC", country: "CA", fullName: "Sorel-Tracy, QC, Canada" },
  { city: "Alma", province: "QC", country: "CA", fullName: "Alma, QC, Canada" },
  { city: "Sept-Îles", province: "QC", country: "CA", fullName: "Sept-Îles, QC, Canada" },
  { city: "Magog", province: "QC", country: "CA", fullName: "Magog, QC, Canada" },
  { city: "Sainte-Julie", province: "QC", country: "CA", fullName: "Sainte-Julie, QC, Canada" },
  { city: "Chambly", province: "QC", country: "CA", fullName: "Chambly, QC, Canada" },
  { city: "Saint-Bruno-de-Montarville", province: "QC", country: "CA", fullName: "Saint-Bruno-de-Montarville, QC, Canada" },
  { city: "Varennes", province: "QC", country: "CA", fullName: "Varennes, QC, Canada" },
  { city: "Beloeil", province: "QC", country: "CA", fullName: "Beloeil, QC, Canada" },
  { city: "Mont-Saint-Hilaire", province: "QC", country: "CA", fullName: "Mont-Saint-Hilaire, QC, Canada" },
  { city: "Candiac", province: "QC", country: "CA", fullName: "Candiac, QC, Canada" },
  { city: "La Prairie", province: "QC", country: "CA", fullName: "La Prairie, QC, Canada" },
  { city: "Saint-Constant", province: "QC", country: "CA", fullName: "Saint-Constant, QC, Canada" },
  { city: "Sainte-Catherine", province: "QC", country: "CA", fullName: "Sainte-Catherine, QC, Canada" },
  { city: "Vaudreuil-Dorion", province: "QC", country: "CA", fullName: "Vaudreuil-Dorion, QC, Canada" },
  { city: "Saint-Eustache", province: "QC", country: "CA", fullName: "Saint-Eustache, QC, Canada" },
  { city: "Sainte-Thérèse", province: "QC", country: "CA", fullName: "Sainte-Thérèse, QC, Canada" },
  { city: "Boisbriand", province: "QC", country: "CA", fullName: "Boisbriand, QC, Canada" },
  { city: "Rosemère", province: "QC", country: "CA", fullName: "Rosemère, QC, Canada" },
  { city: "Deux-Montagnes", province: "QC", country: "CA", fullName: "Deux-Montagnes, QC, Canada" },
  
  // Ontario
  { city: "Toronto", province: "ON", country: "CA", fullName: "Toronto, ON, Canada" },
  { city: "Ottawa", province: "ON", country: "CA", fullName: "Ottawa, ON, Canada" },
  { city: "Mississauga", province: "ON", country: "CA", fullName: "Mississauga, ON, Canada" },
  { city: "Brampton", province: "ON", country: "CA", fullName: "Brampton, ON, Canada" },
  { city: "Hamilton", province: "ON", country: "CA", fullName: "Hamilton, ON, Canada" },
  { city: "London", province: "ON", country: "CA", fullName: "London, ON, Canada" },
  { city: "Markham", province: "ON", country: "CA", fullName: "Markham, ON, Canada" },
  { city: "Vaughan", province: "ON", country: "CA", fullName: "Vaughan, ON, Canada" },
  { city: "Kitchener", province: "ON", country: "CA", fullName: "Kitchener, ON, Canada" },
  { city: "Windsor", province: "ON", country: "CA", fullName: "Windsor, ON, Canada" },
  
  // Other provinces
  { city: "Vancouver", province: "BC", country: "CA", fullName: "Vancouver, BC, Canada" },
  { city: "Calgary", province: "AB", country: "CA", fullName: "Calgary, AB, Canada" },
  { city: "Edmonton", province: "AB", country: "CA", fullName: "Edmonton, AB, Canada" },
  { city: "Winnipeg", province: "MB", country: "CA", fullName: "Winnipeg, MB, Canada" },
  { city: "Saskatoon", province: "SK", country: "CA", fullName: "Saskatoon, SK, Canada" },
  { city: "Regina", province: "SK", country: "CA", fullName: "Regina, SK, Canada" },
  { city: "Halifax", province: "NS", country: "CA", fullName: "Halifax, NS, Canada" },
  
  // USA
  { city: "New York", province: "NY", country: "US", fullName: "New York, NY, USA" },
  { city: "Los Angeles", province: "CA", country: "US", fullName: "Los Angeles, CA, USA" },
  { city: "Chicago", province: "IL", country: "US", fullName: "Chicago, IL, USA" },
  { city: "Houston", province: "TX", country: "US", fullName: "Houston, TX, USA" },
  { city: "Miami", province: "FL", country: "US", fullName: "Miami, FL, USA" },
  { city: "Boston", province: "MA", country: "US", fullName: "Boston, MA, USA" },
  { city: "Seattle", province: "WA", country: "US", fullName: "Seattle, WA, USA" },
  { city: "Las Vegas", province: "NV", country: "US", fullName: "Las Vegas, NV, USA" },
];

// Normalize string for search (remove accents)
const normalizeString = (str) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
};

const searchCitiesLocal = (query) => {
  if (!query || query.trim().length < 2) {
    return [];
  }
  
  const searchTerm = query.toLowerCase().trim();
  const normalizedSearch = normalizeString(searchTerm);
  
  return localCities.filter(cityData => {
    const normalizedCity = normalizeString(cityData.city);
    const normalizedFullName = normalizeString(cityData.fullName);
    
    return normalizedCity.includes(normalizedSearch) ||
           normalizedFullName.includes(normalizedSearch) ||
           cityData.city.toLowerCase().includes(searchTerm);
  }).slice(0, 15);
};

// Export for backwards compatibility
export const searchCities = searchCitiesLocal;

/**
 * Get user's current location using browser geolocation API
 * @returns {Promise<{lat: number, lng: number}>} Coordinates
 */
export const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        let message;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = 'Permission de localisation refusée';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Position non disponible';
            break;
          case error.TIMEOUT:
            message = 'Délai d\'attente dépassé';
            break;
          default:
            message = 'Erreur de géolocalisation';
        }
        reject(new Error(message));
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000 // Cache position for 5 minutes
      }
    );
  });
};

/**
 * Get city from coordinates using reverse geocoding API
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Object|null>} City object or null
 */
export const getCityFromCoordinates = async (lat, lng) => {
  try {
    const response = await fetch(
      `${API_URL}/api/cities/reverse-geocode?lat=${lat}&lng=${lng}`
    );
    
    if (!response.ok) {
      throw new Error('Reverse geocoding failed');
    }
    
    const data = await response.json();
    return data.city || null;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
};

/**
 * Auto-detect user's city using geolocation
 * @returns {Promise<Object|null>} City object or null
 */
export const detectUserCity = async () => {
  try {
    const coords = await getCurrentPosition();
    const city = await getCityFromCoordinates(coords.lat, coords.lng);
    return city;
  } catch (error) {
    console.error('City detection error:', error);
    return null;
  }
};

// Default export
export default searchCitiesAPI;
